import { Link } from "@inertiajs/react";
import { Upload, Download, Plus, Users, ArrowUpRight } from "lucide-react";
import ProgressBar from "@/components/shared/ProgressBar";
import InviteAdminForm from "@/components/schools/setup/InviteAdminForm";
import { DoneSummary } from "@/components/schools/setup/StepPanel";
import { ActionRow, GuideTip, StepIntro, StepNotice } from "@/components/schools/setup/SetupPrimitives";
import { helpHref } from "@/components/schools/setup/steps";

const plural = (n, word) => `${n.toLocaleString("en-CA")} ${word}${n === 1 ? "" : "s"}`;

// ── 4. Students ───────────────────────────────────────────────

export function RosterStep({ step, setup }) {
  const { counts, classrooms, application, paths } = setup;
  if (step.status === "blocked") return null;

  const estimate = application?.estimated_students;
  // Well short of the number on the signup form: probably a class missing.
  const lookShort = counts.students > 0 && estimate && counts.students < estimate * 0.85;
  const actions = [
    { label: counts.students > 0 ? "Upload another spreadsheet" : "Import a spreadsheet", href: paths.import_classrooms, icon: Upload },
    { label: "Download the template", href: paths.classroom_template, external: true, icon: Download },
    { label: "Add a classroom by hand", href: paths.new_classroom, icon: Plus },
  ];

  return (
    <div className="space-y-6">
      {counts.students > 0 ? (
        <DoneSummary title={`${plural(counts.students, "student")} in ${plural(counts.classrooms, "classroom")}`} actions={<ActionRow actions={actions} />}>
          Every student has a reading page and an activation code. Add late arrivals any time before the campaign ends.
        </DoneSummary>
      ) : (
        <>
          <StepIntro>
            <p>
              The spreadsheet needs four columns: first name, last name, classroom and grade. The office can usually export one from your student information system. Use the classroom names families know, like “Room 12” or “Mrs. Lee”.
            </p>
            <p><strong className="font-medium text-foreground">Use full names.</strong> PagePledge shortens last names to an initial wherever families and donors can see them, and tells apart students who share a first name.</p>
          </StepIntro>
          <ActionRow actions={actions} />
        </>
      )}

      {lookShort && (
        <StepNotice tone="warning">
          You told us about {estimate.toLocaleString("en-CA")} students, and {counts.students.toLocaleString("en-CA")} {counts.students === 1 ? "is" : "are"} in so far. Missing a class?
          Uploading again is safe — nobody gets added twice.
        </StepNotice>
      )}

      {classrooms.length > 0 && <ClassroomList classrooms={classrooms} total={counts.classrooms} allPath={paths.classrooms} />}

      {counts.students === 0 && (
        <div className="rounded-xl border border-border p-5">
          <p className="font-medium text-foreground">Someone in the office has the class lists?</p>
          <p className="mb-4 mt-1 text-sm text-muted-foreground">
            Invite your school's office administrator as a co-organizer. {setup.school_approved ? "They'll get a sign-in link and" : "They"} can upload the students from here themselves.
          </p>
          <InviteAdminForm path={paths.members} invitesHeld={!setup.school_approved} />
        </div>
      )}

      {counts.students === 0 && (
        <GuideTip title="Fewer than about 30 students?" href={helpHref("adding-classrooms-manually")} linkLabel="Adding students by hand">
          Adding them by hand is quicker than a spreadsheet. The form jumps back to the start after each student.
        </GuideTip>
      )}
    </div>
  );
}

function ClassroomList({ classrooms, total, allPath }) {
  const shown = classrooms.slice(0, 8);
  return (
    <div className="overflow-hidden rounded-xl border border-border">
      <table className="w-full text-sm font-tabular">
        <thead className="bg-background text-left text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-4 py-2 font-medium">Classroom</th>
            <th className="px-4 py-2 font-medium">Students</th>
            <th className="px-4 py-2 font-medium">Teacher</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {shown.map((classroom) => (
            <tr key={classroom.id}>
              <td className="px-4 py-2">
                <Link href={classroom.path} className="font-medium text-foreground hover:text-primary">{classroom.name}</Link>
                <span className="ml-2 text-xs text-muted-foreground">{classroom.grade}</span>
              </td>
              <td className="px-4 py-2 text-muted-foreground">{classroom.student_count}</td>
              <td className="px-4 py-2">
                {classroom.teachers.length > 0
                  ? <span className="text-muted-foreground">{classroom.teachers.join(", ")}</span>
                  : <span className="text-warning-foreground">None yet</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {total > shown.length && (
        <Link href={allPath} className="block border-t border-border px-4 py-2 text-sm font-medium text-primary hover:bg-secondary/40">
          See all {total} classrooms
        </Link>
      )}
    </div>
  );
}

// ── 5. Teachers ───────────────────────────────────────────────

export function TeachersStep({ step, setup }) {
  const { counts, classrooms, paths } = setup;
  if (step.status === "blocked") return null;

  const covered = counts.classrooms_with_teacher;
  const missing = classrooms.filter((c) => c.teachers.length === 0);
  const actions = [
    { label: covered > 0 ? "Invite more from a spreadsheet" : "Invite from a spreadsheet", href: paths.import_teachers, icon: Upload },
    { label: "Add one classroom at a time", href: paths.classrooms, icon: Users },
  ];

  return (
    <div className="space-y-6">
      {step.status === "done" ? (
        <DoneSummary title="Every classroom has a teacher" actions={<ActionRow actions={actions} />}>
          {plural(counts.teachers, "teacher")} invited. They each got a welcome email with a sign-in link.
        </DoneSummary>
      ) : (
        <>
          {covered > 0 && (
            <ProgressBar
              label="Classrooms with a teacher"
              detail={`${covered} / ${counts.classrooms}`}
              pct={Math.round((covered / counts.classrooms) * 100)}
            />
          )}
          <StepIntro>
            <p>
              The spreadsheet comes filled in with one row per classroom. Add each teacher's name and email, leave a row blank if there's no teacher yet, and upload it.
            </p>
          </StepIntro>
          <ActionRow actions={actions} />
        </>
      )}

      {missing.length > 0 && covered > 0 && (
        <div className="rounded-xl border border-border p-4 text-sm">
          <p className="font-medium text-foreground">Still without a teacher</p>
          <ul className="mt-2 flex flex-wrap gap-2">
            {missing.slice(0, 12).map((classroom) => (
              <li key={classroom.id}>
                <Link href={classroom.path} className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-muted-foreground hover:border-primary/40 hover:text-primary">
                  {classroom.name}
                  <ArrowUpRight className="h-3 w-3" />
                </Link>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-muted-foreground">
            No teacher for a class? You can log its reading yourself, or skip this step.
          </p>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <GuideTip title="What teachers can do">
          Log reading time for their class, print their class's take-home sheets, set classroom goals and rewards, and mark prizes as handed out. They can't see donation details.
        </GuideTip>
        <GuideTip title="Keep home and class reading separate" href="/read-a-thon" linkLabel="From the planning guide">
          Teachers log whole-class reading; families log reading at home. Saying so up front stops the same fifteen minutes being counted twice. The teacher info sheet covers it.
        </GuideTip>
      </div>
    </div>
  );
}

// ── 6. Co-organizers ──────────────────────────────────────────

export function TeamStep({ setup }) {
  const { admins, paths } = setup;

  return (
    <div className="space-y-6">
      <div>
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">School admins</p>
          <Link href={paths.members} className="text-sm font-medium text-primary hover:underline">Manage on the Members page</Link>
        </div>
        <ul className="divide-y divide-border rounded-xl border border-border">
          {admins.map((admin) => (
            <li key={admin.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
              <span className="font-medium text-foreground">{admin.name}{admin.you && <span className="ml-1.5 font-normal text-muted-foreground">(you)</span>}</span>
              <span className="truncate text-muted-foreground">{admin.email}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-xl border border-border p-5">
        <p className="mb-4 font-medium text-foreground">Invite a co-organizer</p>
        <InviteAdminForm path={paths.members} invitesHeld={!setup.school_approved} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <GuideTip title="Give each job an owner" href="/read-a-thon" linkLabel="From the planning guide">
          Family messages, reading records, donation questions and rewards. One person can hold several jobs, but each one needs someone.
        </GuideTip>
        <GuideTip title="Choose admins carefully">
          Admins can do everything, including connecting and disconnecting Square. Save the role for people you'd trust with the school's bank account. Teachers are added separately.
        </GuideTip>
      </div>
    </div>
  );
}

